import { useState, useMemo, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { HOTELS, HOTELS_SEARCH } from '@shared/helpers/routes';
import { parsePassengers } from './parseData';

import StepsWrapper from './StepsWrapper';

import BookingDetail from '../BookingDetail';

import { sendBookingForm } from './sendBookingForm';

import { ID_SITE } from '@shared/conf/env';

import BookingForm from './BookingForm';
import GenericBookingLayout from '@shared/components/GenericBookingLayout';

import {
  getFieldErrorNames,
  toNextStep,
  buildBaseBookingBody,
  returnFormValidator,
} from './helper';

import {
  updateHotelFormikValuesStep1,
  saveSearchDetails,
} from '@shared/redux/actions';

import { usePayeeSelection } from '@shared/components/PayeeSelector/usePayeeSelection';

import { useHotelBookingValuation } from '../helpers/useHotelBookingValuation';
import BookingValuationModalFlow from '../helpers/BookingValuationModalFlow';

const HotelBookingPage = () => {
  const history = useHistory();

  const location = useLocation();

  const {
    storageKey,
    room,
    cancelationFee,
    urlToken,
    id_provider,
    hotelCode,
    idCity,
    id_hotel,
    supplier_code,
    detailTransaction,
    search,
    id_integration,
    user,
    hotelDetails,
    hotelFormikValues,
    isaBuying,
    price: initialPrice,
    hotelsValidateResult: initialHotelsValidateResult,
  } = location.state ?? {};

  const {
    valuationChanges,
    showValuationModal,
    closeModalAndContinue,
    finalError,
    showRetryModal,
    dismissRetry,
    closeModalAndGoBack,
    checkHotelValidationStep,
    priceRef,
    hotelsValidateResultRef,
    cancelationFeeRef,
    price,
    hotelsValidateResult,
  } = useHotelBookingValuation({
    id_integration,
    id_provider,
    urlToken,
    hotelCode,
    id_hotel,
    idCity,
    initialPrice,
    initialCancelationFee: cancelationFee,
    id_transaction: room?.id_transaction,
    initialHotelsValidateResult,
  });
  const ID_SITE_CONST = Number(ID_SITE);

  const [isLoadingPay, setIsLoadingPay] = useState(false);

  const submittingRef = useRef(false);

  const dispatch = useDispatch();

  const payee = usePayeeSelection();

  let compare = user?.entity?.id === 23 || user?.entity?.id === 46;

  const passengerFields = useMemo(
    () =>
      search.passengers?.map((p, idx) => ({
        passengerSequence: idx + 1,
        passengerType: p.id_passenger_type,
        room: p.sequence,
        nombre: '',
        apellido: '',
        numerodedocumento: '',
        pais: '11',
        nacionalidad: '11',
        fechadenacimiento: '',
      })) || [],
    [search.passengers]
  );

  const formik = useFormik({
    initialValues: hotelFormikValues?.step1 ?? {
      email: '',
      emailValidation: '',
      phone: '',
      comments: '',
      passengers: [...passengerFields],
      id_entity: payee.subsidiary?.id || '',
      id_assigned_seller: payee.seller?.id_user || '',
      acceptedTerms: false,
    },
    validationSchema: returnFormValidator(search, payee, ID_SITE_CONST),
    onSubmit: async () => {
      if (submittingRef.current) return;

      submittingRef.current = true;
      setIsLoadingPay(true);

      try {
        const check = await checkHotelValidationStep();
        if (!check?.ok) {
          return;
        }

        dispatch(updateHotelFormikValuesStep1(formik.values));

        await continueBooking();
      } finally {
        submittingRef.current = false;
        setIsLoadingPay(false);
      }
    },
  });

  const continueBooking = async () => {
    const { passengerValues, passengerDetails } = parsePassengers(
      formik.values.passengers
    );

    const { baseBookingBody } = buildBaseBookingBody({
      search,
      hotelDetails,
      idCity,
      formik,
      seller: payee.seller,
      user,
      paxValues: passengerValues,
      paxDetails: passengerDetails,
      room,
      id_product: hotelsValidateResultRef.current?.id_product,
      supplier_code,
      validate: hotelsValidateResultRef.current,
      id_provider,
      id_hotel,
    });

    const commonParams = {
      search,
      hotelDetails,
      values: formik.values,
      location,
      user,
      paxDetails: passengerDetails,
      history,
      detailTransaction,
      id_integration,
      subsidiary: payee.subsidiary,
      seller: payee.seller,
      price: priceRef.current,
      room,
      passengerDetails,
      storageKey,
      id_provider,
      urlToken,
      hotelCode,
      id_hotel,
      idCity,
      cancelationFee: cancelationFeeRef.current,
      validate: hotelsValidateResultRef?.current,
    };

    if (isaBuying) {
      toNextStep({
        ...commonParams,
        baseBookingBody,
        storageKey,
      });
    } else {
      await sendBookingForm({
        ...commonParams,
        baseBookingBody,
        passengerDetails,
        total: priceRef.current?.details?.total,
        dispatch,
      });
    }
  };

  useEffect(() => {
    if (formik.submitCount === 0) return;
    const fieldErrorNames = getFieldErrorNames(formik.errors);
    if (!fieldErrorNames.length) return;

    const el = document.querySelector(`input[name='${fieldErrorNames[0]}']`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [formik.submitCount, formik.errors]);

  const roomsArray = useMemo(() => {
    const passengers = search?.passengers ?? [];
    const roomNumbers = Array.from(
      new Set(passengers.map(p => p.sequence))
    ).sort((a, b) => a - b);

    return roomNumbers.map((roomNum, i) => ({
      title: `Habitación ${i + 1}`,
      id: roomNum,
    }));
  }, [search?.passengers]);

  if (!Object.keys(room ?? {})?.length) {
    history.push(HOTELS);
    return null;
  }

  return (
    <>
      <GenericBookingLayout
        operationType={isaBuying ? 'compra' : 'reserva'}
        searchPath={HOTELS_SEARCH}
        storageKey={storageKey}
        expireTime={600000}
        steps={[
          { title: 'Alojamiento', navigate: null, data: null },
          { title: 'Detalle de alojamiento', navigate: null, data: null },
          { title: 'Reserva', navigate: null, data: null },
        ]}
        stepsWrapperComponent={<StepsWrapper isaBuying={isaBuying} />}
        formComponent={
          <BookingForm
            {...{
              formik,
              roomsArray,
              search,
              cancelationFee,
              hotelDetails,
              isaBuying,
              isLoadingPay,
              payee,
              submittingRef,
              room,
              hotelsValidateResult,
            }}
          />
        }
        detailsComponent={
          <BookingDetail
            room={room}
            hotelDetails={hotelDetails}
            searchData={search}
            isaBuying={isaBuying}
            cancelationFee={cancelationFee}
            price={price}
            hotelsValidateResult={hotelsValidateResult}
          />
        }
      />

      <BookingValuationModalFlow
        showChangesModal={showValuationModal}
        valuationChanges={valuationChanges}
        id_integration={id_integration}
        supplier={hotelsValidateResult.supplier}
        id_product={hotelsValidateResult.id_product}
        isFromHotelBookingStyled={false}
        compare={compare}
        index={1}
        total_nights={search.totalNights}
        total_passangers={search.totalPassengers}
        check_in={search.checkIn}
        onCancelChanges={() => closeModalAndGoBack(history)}
        onConfirmChanges={async () => {
          if (submittingRef.current) return;
          submittingRef.current = true;
          await closeModalAndContinue(continueBooking);
          submittingRef.current = false;
        }}
        showRetryModal={showRetryModal}
        finalError={finalError}
        onRetry={dismissRetry}
        onAbort={() => {
          dispatch(saveSearchDetails(search));
          history.push(HOTELS);
        }}
      />
    </>
  );
};

export default HotelBookingPage;
